import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, refreshSession, setAccessToken, setSessionLostHandler } from "../shared/api/client";
import type { CurrentUser, LoginResponse } from "../shared/types/api";

/**
 * Holds the signed-in user and keeps the access token alive.
 *
 * The token is refreshed proactively, a minute before it expires, rather than reactively on a
 * 401. Waiting for the failure means one request in every session fails and retries, which is
 * visible as a stutter in the interface; refreshing ahead of time means the user never sees it.
 * The reactive path in the API client remains as the safety net for a laptop that was asleep.
 */

interface AuthState {
  user: CurrentUser | null;
  status: "loading" | "authenticated" | "anonymous";
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Ends every session for this employee, not just this browser. */
  signOutEverywhere: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

/** Refresh this long before expiry, so a slow network still completes in time. */
const REFRESH_MARGIN_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const loadUser = useCallback(async () => {
    const me = await api.get<CurrentUser>("/api/v1/me");
    setUser(me);
    setStatus("authenticated");
  }, []);

  // scheduleRefresh and silentRefresh call each other: a refresh schedules the next one. A ref
  // breaks that cycle, so neither has to name the other as a dependency and both stay stable.
  const silentRefreshRef = useRef<() => Promise<boolean>>(async () => false);

  const scheduleRefresh = useCallback(
    (expiresAt: string) => {
      clearTimer();
      const delay = new Date(expiresAt).getTime() - Date.now() - REFRESH_MARGIN_MS;
      // A token already inside the margin is refreshed on the next tick rather than never.
      refreshTimer.current = setTimeout(
        () => void silentRefreshRef.current(),
        Math.max(delay, 1_000),
      );
    },
    [clearTimer],
  );

  /**
   * Renews the access token through the client's single-flight refresh.
   *
   * <p>Deliberately not its own fetch. Refresh tokens rotate and are single-use, so a second
   * concurrent refresh presents a token the first already consumed — indistinguishable from a
   * stolen token being replayed, and correctly met by the server revoking every session. Two
   * tabs, or React StrictMode mounting this effect twice, was enough to trigger it.
   */
  const silentRefresh = useCallback(async () => {
    const session = await refreshSession();
    if (!session) {
      setUser(null);
      setStatus("anonymous");
      clearTimer();
      return false;
    }
    scheduleRefresh(session.expiresAt);
    return true;
  }, [clearTimer, scheduleRefresh]);

  silentRefreshRef.current = silentRefresh;

  // On first load the access token is gone — it only ever lived in memory — but the HttpOnly
  // refresh cookie may still be valid, so a reload restores the session rather than bouncing
  // the user to a login form they did not ask for.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const recovered = await silentRefresh();
      if (cancelled) {
        return;
      }
      if (!recovered) {
        setStatus("anonymous");
        return;
      }
      try {
        await loadUser();
      } catch {
        if (!cancelled) {
          setStatus("anonymous");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Deliberately once on mount: this is session recovery, not a subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The client calls this when a refresh fails mid-flight, so the app can fall back to signed-out
  // instead of leaving a half-dead session on screen.
  useEffect(() => {
    setSessionLostHandler(() => {
      setAccessToken(null);
      setUser(null);
      setStatus("anonymous");
      clearTimer();
    });
    return () => setSessionLostHandler(null);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const response = await api.post<LoginResponse>("/api/v1/auth/login", { email, password });
      setAccessToken(response.accessToken);
      scheduleRefresh(response.expiresAt);
      await loadUser();
    },
    [loadUser, scheduleRefresh],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout");
    } finally {
      // Cleared even if the call failed: the user asked to be signed out, and leaving them
      // apparently signed in because the network hiccuped would be worse than a stale server row.
      setAccessToken(null);
      setUser(null);
      setStatus("anonymous");
      clearTimer();
    }
  }, [clearTimer]);

  /**
   * Revokes every refresh token for this employee.
   *
   * <p>Distinct from ordinary sign-out, which ends only this browser's session. This is what
   * someone reaches for after losing a laptop or using a shared machine, so it is worth having
   * as its own action rather than something to be found in settings.
   */
  const signOutEverywhere = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout-all");
    } finally {
      setAccessToken(null);
      setUser(null);
      setStatus("anonymous");
      clearTimer();
    }
  }, [clearTimer]);

  const can = useCallback(
    (permission: string) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, status, signIn, signOut, signOutEverywhere, refreshUser: loadUser, can }),
    [user, status, signIn, signOut, signOutEverywhere, loadUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
